<template>
  <div class="salon-app salon-staff">
    <!-- Top app bar + booking progress -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.staff.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.staff.stepOf", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <SalonFlowToggle v-if="flowToggle" order="staff" @switch="$emit('switch-flow', $event)" />

      <h2 class="salon-staff-title">{{ $t("salon.staff.heading") }}</h2>

      <!-- SH026 — staff-first: the cards are the roster, not who is free at 11:00, so say
           which appointment they are being picked for and leave availability to the date
           and time steps that follow. -->
      <p v-if="!hasMoment" class="salon-staff-lead">
        {{ $t("salon.staff.lead") }}
      </p>

      <div class="salon-staff-list">
        <div
          v-for="member in staff"
          :key="member.id"
          class="salon-staff-card"
          :class="{
            'salon-staff-card--selected': member.id === selectedStaffId,
            'salon-staff-card--off': !member.is_available,
          }"
          :role="member.is_available ? 'button' : null"
          :tabindex="member.is_available ? 0 : null"
          :aria-pressed="member.is_available ? String(member.id === selectedStaffId) : null"
          :aria-disabled="member.is_available ? null : 'true'"
          @click="select(member)"
          @keydown.enter.prevent="select(member)"
          @keydown.space.prevent="select(member)"
        >
          <img
            v-if="member.image && !brokenImages.includes(member.id)"
            class="salon-staff-avatar"
            :src="member.image"
            :alt="member.name"
            @error="onImageError(member)"
          />
          <span v-else class="salon-staff-avatar salon-staff-avatar--blank" aria-hidden="true">
            {{ initialOf(member.name) }}
          </span>

          <div class="salon-staff-body">
            <h4 class="salon-staff-name">{{ member.name }}</h4>
            <p v-if="member.job_title" class="salon-staff-role">{{ member.job_title }}</p>
            <!-- SH018 — every stylist carries the star, unrated ones included, so the
                 cards read as one column instead of some having a line the rest lack.
                 The score is the average of their published ratings and stands on its
                 own: no ballot count beside it, and an unrated stylist reads "0". -->
            <p class="salon-staff-rating">
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                  fill="currentColor" />
              </svg>
              <span class="salon-staff-score">{{ scoreOf(member) }}</span>
            </p>
          </div>

          <div v-if="hasMoment" class="salon-staff-status">
            <template v-if="member.is_available">
              <span>{{ $t("salon.staff.available") }}</span>
            </template>
            <template v-else-if="member.unavailable_reason === 'booked'">
              <span class="salon-staff-status-line">{{ $t("salon.staff.bookedAt") }}</span>
              <span class="salon-staff-status-line">{{ member.busy_label }}</span>
            </template>
            <template v-else>
              <span>{{ $t("salon.staff.notWorking") }}</span>
            </template>
          </div>
        </div>

        <p v-if="!isLoading && loadFailed" class="salon-empty salon-empty--error">
          {{ $t("salon.staff.loadError") }}
          <button class="salon-retry" type="button" @click="getStaff">{{ $t("salon.staff.retry") }}</button>
        </p>
        <p v-else-if="!isLoading && !staff.length" class="salon-empty">
          {{ emptyText }}
        </p>
      </div>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" :disabled="!hasChoice" @click="goToNextStep">
        <span>{{ nextLabelText }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import SalonFlowToggle from "@/salon/components/FlowToggle.vue";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH008 is the eighth of the nine salon booking steps (Salon_UI/select_staff) — unless
// SH026's channel setting moves it in front of the date, which is what the `step` prop is
// for. The count itself never changes: the same nine questions in a different order.
const STEP = 8;
const TOTAL_STEPS = 9;

export default {
  name: "SalonSelectStaff",
  components: {
    SalonFlowToggle,
    SalonProfileMenu,
    MaterialLoading,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    date: {
      type: String,
      default: "",
    },
    time: {
      type: String,
      default: "",
    },
    durationMin: {
      type: Number,
      default: 0,
    },
    // SH026 — where this screen sits in the nine once the channel has reordered them.
    step: {
      type: Number,
      default: STEP,
    },
    // What the button says: the stylist is the last question on a date-first channel and
    // the first on a staff-first one.
    nextLabel: {
      type: String,
      default: "Review booking",
    },
    // Whether the customer may switch to picking a date first from here.
    flowToggle: {
      type: Boolean,
      default: false,
    },
    // SH042 — what the customer is booking. The stylists offered are only those the
    // merchant assigned to these services; without it the screen would offer the whole
    // roster, which is the bug this fixes.
    services: {
      type: Array,
      default: () => [],
    },
    // Set when the customer is coming back to this screen, so their stylist is still
    // chosen rather than the list opening blank.
    initialStaffId: {
      type: [Number, String],
      default: "",
    },
  },
  emits: ["back", "selected", "profile", "switch-flow"],
  data() {
    return {
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      loadFailed: false,
      staff: [],
      // SH026 — whether the list was judged against a date and time at all. False on a
      // staff-first channel, where nobody can be busy for an appointment that has no
      // time yet.
      hasMoment: true,
      brokenImages: [],
      // SH034 — "" is "nothing chosen yet", and nothing is preselected: the customer now
      // names their stylist, so the choice has to be theirs rather than a card the screen
      // opens already ticked.
      selectedStaffId: this.initialStaffId === "" ? "" : Number(this.initialStaffId),
    };
  },
  computed: {
    progressWidth() {
      return ((this.step / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },

    hasChoice() {
      return this.selectedStaffId !== "";
    },

    selectedStaff() {
      return this.staff.find((member) => member.id === this.selectedStaffId) || null;
    },

    // SH042 — the services themselves, not their add-ons: an add-on is a modifier option
    // and its id belongs to a different table than the one the merchant ticks skills in.
    serviceIds() {
      return this.services
        .map((service) => Number(service && service.id))
        .filter((id) => Number.isInteger(id) && id > 0);
    },

    serviceNames() {
      return this.services
        .map((service) => (service && service.name) || "")
        .filter((name) => name !== "")
        .join(" + ");
    },

    // The list can now be empty for two different reasons, and only one of them is the
    // outlet's fault. Naming the service tells the customer what to go back and change.
    emptyText() {
      return this.serviceIds.length && this.serviceNames
        ? this.$t("salon.staff.emptyForServices", { services: this.serviceNames })
        : this.$t("salon.staff.empty");
    },

    // SH046 — BookingFlow passes an already translated label, which is shown as given; only
    // the prop's English default is looked up, so the screen still follows the language
    // when it is mounted without one.
    nextLabelText() {
      const keys = {
        Continue: "salon.staff.continue",
        "Review booking": "salon.staff.reviewBooking",
      };

      return keys[this.nextLabel] ? this.$t(keys[this.nextLabel]) : this.nextLabel;
    },
  },
  created() {
    this.getStaff();
  },
  methods: {
    async getStaff() {
      if (!this.branchCode) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        // SH026 — with no date and time yet, the endpoint answers with the roster. Sending
        // undefined drops the keys rather than posting empty strings, which the API would
        // reject as a half-given moment.
        const response = await SalonBookingService.getAvailableStaff({
          branch_code: this.branchCode,
          date: this.date || undefined,
          time: this.time || undefined,
          duration_min: this.durationMin || undefined,
          // SH042 — dropped rather than sent empty, so an outlet whose services carry no
          // id still gets the roster instead of an empty screen.
          service_ids: this.serviceIds.length ? this.serviceIds : undefined,
        });

        if (response.data.result.isSuccess) {
          const data = response.data.data;

          this.staff = data.staff ?? [];
          this.hasMoment = data.has_moment !== false;
          this.dropUnavailableSelection();
        } else {
          this.staff = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // An outlet with no stylists configured and a broken endpoint both render as an
        // empty list; only one of them deserves a Retry.
        this.staff = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Coming back from a later step onto a reloaded list, the stylist chosen before may
    // have been booked in the meantime.
    dropUnavailableSelection() {
      const chosen = this.selectedStaff;

      if (!chosen || !chosen.is_available) {
        this.selectedStaffId = "";
      }
    },

    select(member) {
      if (!member.is_available) {
        return;
      }

      this.selectedStaffId = member.id;
    },

    initialOf(name) {
      return (name || "?").trim().charAt(0).toUpperCase();
    },

    // The API sends `rating: null` for a stylist nobody has rated yet, so the card has to
    // supply the zero itself rather than reading through a missing object. Number() drops
    // a trailing ".0" on the way out, so a clean average prints as "4" and an unrated
    // stylist as "0" rather than "4.0" / "0.0".
    scoreOf(member) {
      return String(Number(member.rating?.average) || 0);
    },

    onImageError(member) {
      if (!this.brokenImages.includes(member.id)) {
        this.brokenImages.push(member.id);
      }
    },

    goToNextStep() {
      if (!this.hasChoice) {
        return;
      }

      // SH009 reviews the whole booking, so it needs the stylist by name as well as id —
      // M925's calendar event stores the name, not the id.
      this.$emit("selected", {
        staff_id: this.selectedStaffId,
        staff_name: this.selectedStaff.name,
        job_title: this.selectedStaff.job_title,
      });
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-staff {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

/* This screen titles its bar in the display face, unlike the earlier steps. */
.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 12px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-variant);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary);
  border-radius: 999px;
  transition: width 0.5s ease-out;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  padding: var(--salon-stack-lg) var(--salon-container-padding) 140px;
}

.salon-staff-title {
  margin: 0 0 var(--salon-stack-lg);
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: var(--salon-on-surface);
}

/* SH026 — the staff-first lead line. Sits under a headline that already carries its own
   bottom margin, so it only closes the gap to the list below it. */
.salon-staff-lead {
  margin: calc(var(--salon-stack-lg) * -1 + var(--salon-stack-sm)) 0 var(--salon-stack-lg);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Stylist cards ─────────────────────────────────────────────── */
.salon-staff-list {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-staff-card {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-variant);
  border-radius: 12px;
  box-shadow: var(--salon-shadow-soft);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-staff-card:hover:not(.salon-staff-card--off) {
  border-color: var(--salon-on-surface-variant);
}

.salon-staff-card:active:not(.salon-staff-card--off) {
  transform: scale(0.98);
}

.salon-staff-card--selected {
  border-color: var(--salon-gold);
  border-width: 1.5px;
  box-shadow: 0 0 15px rgba(201, 162, 39, 0.3);
}

.salon-staff-card--off {
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-staff-avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 999px;
}

.salon-staff-avatar--blank {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
}

.salon-staff-body {
  flex: 1;
  min-width: 0;
}

.salon-staff-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-staff-role {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-staff-rating {
  display: flex;
  gap: 6px;
  align-items: center;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-secondary-container);
}

.salon-staff-score {
  font-weight: 500;
  color: var(--salon-on-surface);
}

.salon-staff-status {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  text-align: right;
}

.salon-staff-status-line {
  display: block;
}

.salon-empty {
  padding: 24px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-empty--error {
  color: var(--salon-error);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(28, 28, 30, 0.18);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
